import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ProjectSummary } from '@/types'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { PageHero } from '@/components/sections/page-hero'
import { CtaBand } from '@/components/sections/cta-band'
import { ProjectCard } from '@/components/projects/project-card'

import {
    REVALIDATE_SECONDS,
    RETRY_SECONDS,
    getProjects,
    loadContent
} from '@/lib/content'

import { contactHref } from '@/constants/site'

interface ProjectsPageProps {
    projects: Array<ProjectSummary>
}

interface ServiceFilter {
    id: string
    slug: string
    title: string
    count: number
}

const ALL = 'sve'

const chipClasses = (active: boolean): string => cn(
    'inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
    active ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground hover:border-primary/50'
)

const countClasses = (active: boolean): string => cn(
    'rounded-full px-1.5 text-xs',
    active ? 'bg-white/20' : 'bg-muted text-muted-foreground'
)

const ProjectsPage: FC<ProjectsPageProps> = ({ projects }): ReactNode => {
    const router = useRouter()
    const [active, setActive] = useState<string>(ALL)

    const filters = useMemo((): Array<ServiceFilter> => {
        const byService = new Map<string, ServiceFilter>()

        for (const project of projects) {
            if (!project.service_id || !project.service_slug || !project.service_title) continue

            const existing = byService.get(project.service_slug)

            if (existing) existing.count += 1
            else byService.set(project.service_slug, { id: project.service_id, slug: project.service_slug, title: project.service_title, count: 1 })
        }

        return Array.from(byService.values())
    }, [projects])

    useEffect(() => {
        if (!router.isReady) return

        const requested = typeof router.query.usluga === 'string' ? router.query.usluga : ALL

        setActive(filters.some(filter => filter.slug === requested) ? requested : ALL)
    }, [router.isReady, router.query.usluga, filters])

    const selectFilter = (slug: string) => {
        setActive(slug)

        void router.replace(
            { pathname: router.pathname, query: slug === ALL ? {} : { usluga: slug } },
            undefined,
            { shallow: true, scroll: false }
        )
    }

    const visible = active === ALL ? projects : projects.filter(project => project.service_slug === active)

    return (
        <Layout>
            <Seo
                title='Projekti'
                description='Prethodno realizovani građevinski projekti, adaptacije i renoviranja našeg tima u Sarajevu i okolini.'
            />

            <PageHero
                eyebrow='Projekti'
                title='Prethodno realizovani projekti našeg tima'
                description='Pregled naših najznačajnijih radova realizovanih po najvišim standardima. Izaberite vrstu radova ili otvorite projekat za više detalja.'
            />

            <section id='portfolio' aria-label='Portfolio projekata' className='py-14 lg:py-20'>
                <Container>
                    {projects.length === 0 ? (
                        <div className='mx-auto max-w-xl py-16 text-center'>
                            <h2 className='text-3xl font-bold tracking-tight'>
                                Projekti uskoro
                            </h2>

                            <p className='mt-4 text-lg leading-relaxed text-muted-foreground'>
                                Pripremamo fotografije naših radova. Do tada nam pošaljite upit i rado ćemo Vam pokazati primjere slične Vašem projektu.
                            </p>

                            <Link href={contactHref} className={buttonVariants({ size: 'lg', className: 'mt-8' })}>
                                Pošaljite upit
                            </Link>
                        </div>
                    ) : (
                        <>
                            {filters.length > 1 && (
                                <div role='group' aria-label='Filtriraj projekte po vrsti radova' className='-mx-5 flex gap-2 overflow-x-auto px-5 pb-2 no-scrollbar sm:mx-0 sm:flex-wrap sm:justify-center sm:overflow-visible sm:px-0'>
                                    <button
                                        type='button'
                                        aria-pressed={active === ALL}
                                        onClick={() => selectFilter(ALL)}
                                        className={chipClasses(active === ALL)}
                                    >
                                        Svi projekti
                                        <span className={countClasses(active === ALL)}>
                                            {projects.length}
                                        </span>
                                    </button>

                                    {filters.map(filter => (
                                        <button
                                            key={filter.slug}
                                            type='button'
                                            aria-pressed={active === filter.slug}
                                            onClick={() => selectFilter(filter.slug)}
                                            className={chipClasses(active === filter.slug)}
                                        >
                                            <span data-mdlfy-cms={`services:${filter.id}:title`}>
                                                {filter.title}
                                            </span>

                                            <span className={countClasses(active === filter.slug)}>
                                                {filter.count}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            <p aria-live='polite' className='sr-only'>
                                {`Prikazano projekata: ${visible.length}`}
                            </p>

                            <div className='mt-10 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3'>
                                {visible.map((project, index) => (
                                    <ProjectCard
                                        key={project.id}
                                        project={project}
                                        headingLevel='h2'
                                        preload={index < 3}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </Container>
            </section>

            <CtaBand />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ProjectsPageProps> = async () => {
    const projects = await loadContent('projects', getProjects, [])

    return {
        props: {
            projects: projects.data
        },
        revalidate: projects.failed ? RETRY_SECONDS : REVALIDATE_SECONDS
    }
}

export default ProjectsPage
